/**
 * Screenshot-Anzeige mit Vektor-Annotationen als SVG-Overlay (F5).
 * Das Bild kommt per Header-Auth (Object-URL); Rechtecke/Pfeile liegen
 * als Vektor-Daten in Bild-Pixel-Koordinaten darüber.
 */

import { useState } from "react"
import { useAuthImage } from "@/lib/useAuthImage"
import type { Screenshot } from "@/types"

interface Props {
  reportId: number
  screenshot: Screenshot
  className?: string
  /** true: Bild füllt den Container (object-contain, z. B. Fenster-Tab) */
  gross?: boolean
}

export function ScreenshotBild({ reportId, screenshot, className, gross = false }: Props) {
  const url = useAuthImage(reportId, screenshot.id)
  const [masse, setMasse] = useState<{ w: number; h: number } | null>(null)

  if (!url) {
    return (
      <div className={`flex items-center justify-center bg-muted text-xs text-muted-foreground ${gross ? "h-full w-full" : ""} ${className ?? ""}`}>
        Lade Bild …
      </div>
    )
  }

  return (
    <div className={`relative ${gross ? "h-full w-full" : ""} ${className ?? ""}`}>
      <img
        src={url}
        alt={`Screenshot ${screenshot.reihenfolge + 1}`}
        className={
          gross
            ? "block h-full w-full object-contain"
            : "block h-auto w-full rounded-md border"
        }
        onLoad={(e) =>
          setMasse({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
        }
      />
      {/* preserveAspectRatio-Default (xMidYMid meet) skaliert exakt wie
          object-contain; im Kompakt-Modus entspricht die Box ohnehin dem
          Bild-Seitenverhältnis — Overlay bleibt in beiden Fällen deckungsgleich */}
      {masse && screenshot.annotationen && screenshot.annotationen.length > 0 && (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox={`0 0 ${masse.w} ${masse.h}`}
        >
          {screenshot.annotationen.map((a, i) =>
            a.art === "rechteck" ? (
              <rect
                key={i}
                x={Math.min(a.x, a.x + a.w)}
                y={Math.min(a.y, a.y + a.h)}
                width={Math.abs(a.w)}
                height={Math.abs(a.h)}
                fill="none"
                stroke="#dc2626"
                strokeWidth={3}
              />
            ) : a.art === "kreis" ? (
              <ellipse
                key={i}
                cx={Math.min(a.x, a.x + a.w) + Math.abs(a.w) / 2}
                cy={Math.min(a.y, a.y + a.h) + Math.abs(a.h) / 2}
                rx={Math.abs(a.w) / 2}
                ry={Math.abs(a.h) / 2}
                fill="none"
                stroke="#dc2626"
                strokeWidth={3}
              />
            ) : a.art === "buchstabe" || a.art === "zahl" ? (
              <g key={i}>
                <circle
                  cx={a.x}
                  cy={a.y}
                  r={Math.max(16, masse.w * 0.016)}
                  fill={a.art === "zahl" ? "#2563eb" : "#dc2626"}
                />
                <text
                  x={a.x}
                  y={a.y}
                  fill="#ffffff"
                  fontSize={
                    Math.max(16, masse.w * 0.016) * ((a.zeichen?.length ?? 1) > 1 ? 0.95 : 1.25)
                  }
                  fontWeight="bold"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {a.zeichen ?? "?"}
                </text>
              </g>
            ) : (
              <line
                key={i}
                x1={a.x}
                y1={a.y}
                x2={a.x + a.w}
                y2={a.y + a.h}
                stroke="#dc2626"
                strokeWidth={3}
                markerEnd={`url(#pfeilspitze-${reportId}-${screenshot.id})`}
              />
            )
          )}
          <defs>
            <marker
              id={`pfeilspitze-${reportId}-${screenshot.id}`}
              markerWidth="8"
              markerHeight="8"
              refX="6"
              refY="4"
              orient="auto"
            >
              <path d="M0,0 L8,4 L0,8 z" fill="#dc2626" />
            </marker>
          </defs>
        </svg>
      )}
    </div>
  )
}
